import React, { useState, useEffect, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import HomePage from './HomePage';
import AboutPage from './AboutPage';
import LoginPage from './LoginPage';
import StudentLoginPage from './StudentLoginPage';
import DashboardPage from './DashboardPage';
import SignUpPage from './SignUpPage';
import TeacherProfilePage from './TeacherProfilePage';
import ContactPage from './ContactPage';
import FindTeachersPage from './FindTeachersPage';
import StudentSignupPage from './StudentSignupPage';
import GoogleSignUpPage from './GoogleSignUpPage';
import StudentDashboard from './StudentDashboard';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import ChatHistoryPage from './ChatHistoryPage';
import Navbar from './components/Navbar';
import Layout from './components/Layout';
import { supabase, supabaseConfigError } from './supabase';
import './theme.css';

const AIAssistantPage = lazy(() => import('./AIAssistantPage'));

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const pendingOAuthIntent = localStorage.getItem('pendingOAuthIntent');
    const hash = window.location.hash;

    if (
      pendingOAuthIntent === 'google-auth' &&
      location.pathname === '/' &&
      hash.includes('access_token=')
    ) {
      navigate(`/google-signup${hash}`, { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    let isMounted = true;

    if (supabaseConfigError) {
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const initializeSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!isMounted) return;
        setUser(session?.user ?? null);
      } catch (error) {
        console.error('Unable to restore auth session.', error);

        if (!isMounted) return;
        setUser(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeSession();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
          color: '#0f172a',
          fontFamily: "'Lexend', system-ui, sans-serif",
          fontSize: '1rem',
          fontWeight: 600,
        }}
      >
        Loading LankaTutor...
      </div>
    );
  }

   if (supabaseConfigError) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
          padding: '2rem',
        }}
      >
        <div
          style={{
            width: 'min(38rem, 100%)',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '1rem',
            boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
            padding: '1.5rem',
            color: '#0f172a',
            fontFamily: "'Lexend', system-ui, sans-serif",
          }}
        >
          <h1 style={{ margin: '0 0 0.75rem', fontSize: '1.5rem' }}>Configuration required</h1>
          <p style={{ margin: '0 0 0.75rem', lineHeight: 1.6 }}>{supabaseConfigError}</p>
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Add <code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code>{' '}
            in Vercel, then redeploy the site.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {!user && location.pathname !== '/' && !location.pathname.startsWith('/teacher/') && <Navbar />}
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/student-login" element={<StudentLoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/student-signup" element={<StudentSignupPage />} />
          <Route path="/google-signup" element={<GoogleSignUpPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/student-dashboard" element={<StudentDashboard />} />
          <Route path="/find-teachers" element={<FindTeachersPage />} />
          <Route path="/teacher/:teacherId" element={<TeacherProfilePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/ai-assistant"
            element={
              <Suspense
                fallback={
                  <div
                    style={{
                      minHeight: '40vh',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#334155',
                      fontFamily: "'Lexend', system-ui, sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    Loading AI Assistant...
                  </div>
                }
              >
                <AIAssistantPage />
              </Suspense>
            }
          />
          <Route path="/chat-history" element={<ChatHistoryPage />} />
        </Routes>
      </Layout>
    </>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <Router future={{ v7_startTransition: true }}>
      <App />
    </Router>
  </React.StrictMode>
);
